/**
 * Sidney's Terms and Privacy Policy, as supplied.
 *
 * ---------------------------------------------------------------------------
 * THIS IS TRANSCRIPTION, NOT AUTHORSHIP.
 *
 * The wording below is Sidney's, reproduced from the two source documents. Nothing here
 * was rewritten, summarised, modernised, shortened, or corrected. Where a document
 * contains something that looks like a typo, an inconsistency, or a gap, it is
 * reproduced AS SUPPLIED and reported separately for Sidney to decide on -- a silent fix
 * to a legal document is a change to a legal document.
 *
 * The structure (headings, numbering, lists, defined-term lead-ins) is carried across
 * because it is part of how the documents read. The styling is ours; the words are not.
 *
 * ---------------------------------------------------------------------------
 * WHAT WAS DELIBERATELY NOT PUBLISHED, AND WHY
 *
 * Both source documents end with an internal checklist for the people preparing them --
 * "Implementation Checklist -- Remove Before Publishing" in the Privacy Policy, "Launch
 * Checklist (Remove Before Publishing)" in the Terms. The Terms also carry two inline
 * notes addressed to counsel rather than to a customer: a "Drafting note" after the
 * opening paragraph, and a "Counsel decision required" note in Section 15.
 *
 * None of that is rendered. All four are marked, in the documents themselves, as
 * material to remove before publishing, and they describe open questions about Metered
 * Mining's own launch readiness. Publishing them on a customer-facing page would expose
 * internal drafting to customers.
 *
 * This is an OMISSION OF INTERNAL NOTES, not an edit to the legal terms: no substantive
 * provision was touched. It is listed in the report accompanying this change so Bertrand
 * can direct otherwise. If they should appear, they go back in here.
 *
 * ---------------------------------------------------------------------------
 * TO UPDATE A DOCUMENT: replace the text here and update the date. There is no CMS, and
 * Phase 1 does not authorise one.
 */

/** A list item, optionally led by a defined term the source sets in bold. */
export type LegalListItem = string | { lead: string; rest: string };

export type LegalBlock =
  | { kind: 'paragraph'; text: string }
  /** A sub-heading inside a numbered section, e.g. "1.1 Virtual hashrate rental". */
  | { kind: 'subheading'; text: string }
  | { kind: 'list'; items: LegalListItem[] }
  /** A line the source sets apart and emphasises, such as the delivery-target formula. */
  | { kind: 'callout'; text: string }
  /** Contact details, kept as a block so the lines do not run together. */
  | { kind: 'address'; lines: string[] };

export interface LegalSection {
  heading: string;
  blocks: LegalBlock[];
}

export interface LegalDocumentContent {
  title: string;
  /** "Effective date" or "Last updated" -- whichever the source uses. */
  dateLabel: string;
  /** Reproduced exactly as supplied. */
  date: string;
  intro: LegalBlock[];
  sections: LegalSection[];
}

// --- Terms -------------------------------------------------------------------

export const TERMS: LegalDocumentContent = {
  title: 'Metered Mining Virtual Hashrate Rental Terms',
  dateLabel: 'Effective date',
  date: 'August 9, 2026',
  intro: [
    {
      kind: 'paragraph',
      text: 'These Virtual Hashrate Rental Terms (the “Terms”) govern purchases of virtual Bitcoin mining hashrate from The Metered, LLC, doing business as Metered Mining (“Metered Mining,” “we,” “us,” or “our”). By placing an order, creating an account, or using the service, you agree to these Terms and our Privacy Policy.',
    },
  ],
  sections: [
    {
      heading: '1. The Service',
      blocks: [
        { kind: 'subheading', text: '1.1 Virtual hashrate rental' },
        {
          kind: 'paragraph',
          text: 'A “Virtual Miner” is a fixed-term rental of a stated quantity of Bitcoin SHA-256 mining hashrate. It is not the sale, lease, or transfer of a particular physical mining machine. During the purchased term, Metered Mining allocates and directs the ordered amount of mining work toward solo Bitcoin mining for the Bitcoin payout address supplied by you.',
        },
        {
          kind: 'paragraph',
          text: 'If the valid mining work allocated to your order produces a Bitcoin block that is accepted by the Bitcoin network and credited by the applicable mining pool, the pool or Bitcoin network will pay the resulting block reward according to the pool’s payout process and the payout address registered to your order. Except where expressly disclosed in a separate written agreement, Metered Mining does not receive or take custody of your mining rewards.',
        },
        { kind: 'subheading', text: '1.2 No guaranteed reward or earnings' },
        {
          kind: 'paragraph',
          text: 'Solo Bitcoin mining is probabilistic. Purchasing hashrate does not guarantee that you will find a block, receive Bitcoin, recover your purchase price, or earn a profit. For the rental sizes and terms offered, receiving no mining reward may be the most likely outcome.',
        },
        {
          kind: 'paragraph',
          text: 'You are purchasing mining work and the associated probability of finding a block—not Bitcoin, an investment, a security, a deposit account, an interest-bearing product, or a guaranteed income stream. Any calculator, probability, estimated return, network difficulty, historical result, share count, best share, or similar information is illustrative only and may change or prove inaccurate.',
        },
        { kind: 'subheading', text: '1.3 Combined orders' },
        {
          kind: 'paragraph',
          text: 'Multiple Virtual Miners purchased under one order may be administered as one combined hashrate allocation with one term and one payout address. Combining allocations increases the total hashrate directed to that address but does not guarantee a reward.',
        },
        { kind: 'subheading', text: '1.4 No ownership of equipment' },
        {
          kind: 'paragraph',
          text: 'You receive only the contractual right to the mining work stated in your order. You acquire no title to, security interest in, possession of, or right to select, inspect, control, or remove any physical miner, server, pool account, facility, electrical equipment, or other infrastructure used to provide the service.',
        },
      ],
    },
    {
      heading: '2. Customer Account and Eligibility',
      blocks: [
        { kind: 'subheading', text: '2.1 Eligibility' },
        {
          kind: 'paragraph',
          text: 'You must be at least 18 years old and legally able to enter into a binding contract. You may not use the service if you are located in, ordinarily resident in, or acting on behalf of a person or entity in a jurisdiction where the service is prohibited, or if you are subject to applicable sanctions or restricted-party rules.',
        },
        { kind: 'subheading', text: '2.2 Accurate information and account security' },
        {
          kind: 'paragraph',
          text: 'You must provide accurate, current information and keep it updated. You are responsible for protecting your account credentials and for activity conducted through your account. Notify us promptly at hello@meteredmining.io if you suspect unauthorized access.',
        },
        { kind: 'subheading', text: '2.3 Compliance review' },
        {
          kind: 'paragraph',
          text: 'We may request information reasonably necessary to verify identity, payment authorization, location, sanctions compliance, fraud risk, or lawful use. We may reject, pause, or cancel an order if required by law, a payment provider, a mining pool, or our reasonable compliance procedures. If we cancel before providing service and the cancellation is not caused by fraud, abuse, or unlawful activity, we will refund the unused portion of the order.',
        },
      ],
    },
    {
      heading: '3. Bitcoin Payout Address',
      blocks: [
        { kind: 'subheading', text: '3.1 Customer-controlled wallet required' },
        {
          kind: 'paragraph',
          text: 'You must supply a valid Bitcoin payout address that you control before mining begins. Metered Mining does not provide a custodial wallet and does not hold private keys for you. We may validate the address format, but we cannot confirm that you own or control the address.',
        },
        { kind: 'subheading', text: '3.2 Your responsibility' },
        {
          kind: 'paragraph',
          text: 'You are solely responsible for the accuracy, compatibility, security, and control of your payout address and wallet. Bitcoin transactions are generally irreversible. A reward sent to the address you supplied is treated as properly delivered even if the address was entered incorrectly, belongs to another person, is unsupported by your wallet provider, or becomes inaccessible because private keys or credentials were lost.',
        },
        { kind: 'subheading', text: '3.3 Address changes' },
        {
          kind: 'paragraph',
          text: 'You may request or make a payout-address change through your customer dashboard or by contacting hello@meteredmining.io. A change applies only after it is accepted and implemented. It cannot alter work already submitted, a coinbase transaction already created, or a reward already paid. We may require security verification and temporarily pause an address change to prevent fraud.',
        },
      ],
    },
    {
      heading: '4. Activation, Term, and Delivery',
      blocks: [
        { kind: 'subheading', text: '4.1 Activation and term' },
        {
          kind: 'paragraph',
          text: 'Your rental begins when the order is activated, normally after cleared payment and completion of any required review. The applicable hashrate, duration, activation time, and scheduled expiration will appear in your order confirmation or dashboard.',
        },
        { kind: 'subheading', text: '4.2 How delivery is measured' },
        {
          kind: 'paragraph',
          text: 'Hashrate is delivered as valid, difficulty-weighted SHA-256 work allocated to your order and measured over the full rental term. Pool-side or short-interval hashrate may fluctuate substantially because mining is probabilistic and work may be delivered in intervals or bursts. Live dashboard readings are estimates; the controlling measurement is the cumulative valid difficulty-weighted work recorded for the order over its term.',
        },
        {
          kind: 'paragraph',
          text: 'Unless the order expressly states otherwise, the delivery target equals:',
        },
        {
          kind: 'callout',
          text: 'Purchased hashrate × elapsed rental time, adjusted for any extension, refund, or other credit provided under these Terms.',
        },
        {
          kind: 'paragraph',
          text: 'Rejected, stale, duplicate, invalid, or otherwise uncredited shares do not count as delivered work unless the rejection was caused by a reporting issue and the underlying work can be independently verified.',
        },
        { kind: 'subheading', text: '4.3 Delivery shortfall' },
        {
          kind: 'paragraph',
          text: 'If, due to circumstances within our reasonable control, cumulative delivered work at expiration is less than the purchased amount, Metered Mining will, at its option: (a) extend the rental long enough to deliver the verified shortfall; (b) issue a service credit; or (c) refund the price attributable to the undelivered portion. This is your exclusive contractual remedy for an ordinary delivery shortfall, except where applicable law requires otherwise.',
        },
        { kind: 'subheading', text: '4.4 Pool and network records' },
        {
          kind: 'paragraph',
          text: 'Mining pool records, our allocation records, and blockchain data may be used to verify delivery and rewards. In case of a dashboard display error, the underlying pool, allocation, and blockchain records control. We may correct obvious reporting or calculation errors.',
        },
      ],
    },
    {
      heading: '5. Blocks, Rewards, and Bitcoin Network Events',
      blocks: [
        { kind: 'subheading', text: '5.1 Valid block requirement' },
        {
          kind: 'paragraph',
          text: 'A displayed candidate block, best share, or share above a displayed difficulty does not by itself guarantee payment. A reward is earned only if the work is valid, timely submitted, accepted under the applicable pool rules, accepted by the Bitcoin network, and remains payable after the pool’s confirmation and validation process.',
        },
        { kind: 'subheading', text: '5.2 Reward amount' },
        {
          kind: 'paragraph',
          text: 'Mining work is submitted through PowerPool.io (“PowerPool”). The reward, if any, is determined by the Bitcoin protocol and PowerPool’s applicable rules and validation process. The reward may include the block subsidy and transaction fees, less PowerPool’s 1% pool fee and any Bitcoin network transaction fee disclosed or imposed when a payout is made. Metered Mining does not control network difficulty, block subsidy halvings, transaction fees, confirmation timing, PowerPool validation, or the market value of Bitcoin.',
        },
        { kind: 'subheading', text: '5.3 Reorganizations and invalid blocks' },
        {
          kind: 'paragraph',
          text: 'A block may become stale, orphaned, invalid, reversed by a chain reorganization, or rejected by a pool or the Bitcoin network. Metered Mining is not responsible for a reward that is not created or paid because of those events, unless caused by our failure to deliver the purchased work as described in Section 4.',
        },
        { kind: 'subheading', text: '5.4 Taxes on rewards' },
        {
          kind: 'paragraph',
          text: 'You are solely responsible for determining and paying any tax arising from mining rewards, Bitcoin ownership, disposition of Bitcoin, or use of the service. We do not provide tax, legal, or investment advice.',
        },
      ],
    },
    {
      heading: '6. Prices, Taxes, and Payment',
      blocks: [
        { kind: 'subheading', text: '6.1 Pricing' },
        {
          kind: 'paragraph',
          text: 'Prices are displayed in U.S. dollars unless stated otherwise. The price and term shown at checkout apply to that order once payment is completed. We may change prices for future purchases.',
        },
        { kind: 'subheading', text: '6.2 Taxes and fees' },
        {
          kind: 'paragraph',
          text: 'Applicable sales, use, value-added, goods-and-services, or similar taxes may be added or collected as required by law. You are responsible for taxes not collected by us, other than taxes imposed on our net income.',
        },
        { kind: 'subheading', text: '6.3 Payment processing' },
        {
          kind: 'paragraph',
          text: 'Payments are processed through SaturnShift.io (“SaturnShift”). SaturnShift’s terms and privacy practices may also apply. Metered Mining does not directly store complete payment-card information. You authorize Metered Mining and SaturnShift to charge the payment method selected for the amount presented at checkout, including applicable taxes.',
        },
        { kind: 'subheading', text: '6.4 Finality of blockchain payments' },
        {
          kind: 'paragraph',
          text: 'If Bitcoin or another permitted digital asset is accepted as payment, the exchange rate, confirmation requirements, payment deadline, and network fees shown at checkout apply. Blockchain payments are not reversible. Any approved refund will be issued in the method and dollar value stated in the refund policy or required by law, and not necessarily in the same quantity of digital assets originally paid.',
        },
      ],
    },
    {
      heading: '7. Expiration and Additional Purchases',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Virtual Hashrate rentals do not renew automatically. Each rental ends at its scheduled expiration without further charge. If you wish to continue service, you must make a separate purchase at the price and terms then offered. Purchasing another term before expiration may allow the allocation and statistics to continue without a gap, but capacity is not guaranteed until the new purchase is accepted and paid.',
        },
      ],
    },
    {
      heading: '8. Cancellations and Refunds',
      blocks: [
        { kind: 'subheading', text: '8.1 All sales final; customer cancellation not accepted' },
        {
          kind: 'paragraph',
          text: 'All purchases are final once payment is accepted. Customer-requested cancellations are not accepted before or after activation. Purchasing by mistake, changing your mind, or failing to provide or maintain a usable payout address does not create a cancellation or refund right. If Metered Mining cannot accept or activate an order, we will cancel it and refund the amount paid, less any nonrefundable blockchain fee disclosed before purchase and permitted by law.',
        },
        { kind: 'subheading', text: '8.2 Limited refund exceptions' },
        {
          kind: 'paragraph',
          text: 'Because electricity, capacity, and mining work are reserved and consumed in providing the service, purchases are noncancelable and nonrefundable except: (a) for a verified delivery shortfall under Section 4.3; (b) if Metered Mining cannot activate the order; (c) where we expressly approve a refund in writing; or (d) where applicable law requires otherwise.',
        },
        { kind: 'subheading', text: '8.3 No refund based on mining outcome' },
        {
          kind: 'paragraph',
          text: 'Failure to find a block, a decrease in Bitcoin’s price, an increase in network difficulty, a change in transaction fees, a halving, or dissatisfaction with the probabilistic outcome is not a service failure and does not qualify for a refund.',
        },
        { kind: 'subheading', text: '8.4 Refund timing and method' },
        {
          kind: 'paragraph',
          text: 'Approved refunds are sent to the original payment method when reasonably possible. Processing times depend on the payment provider. We may require reasonable verification before sending a refund to a different destination.',
        },
      ],
    },
    {
      heading: '9. Payment Reversals and Fraud',
      blocks: [
        {
          kind: 'paragraph',
          text: 'If a payment is reversed, disputed, charged back, unauthorized, or determined to be fraudulent, we may suspend or terminate the associated service and account to the extent permitted by law. You remain responsible for valid amounts owed and reasonable costs caused by fraudulent or abusive activity, but nothing in these Terms limits lawful billing-error or chargeback rights.',
        },
      ],
    },
    {
      heading: '10. Operations, Interruptions, and Force Majeure',
      blocks: [
        { kind: 'subheading', text: '10.1 Operational changes' },
        {
          kind: 'paragraph',
          text: 'We may use different miners, facilities, pools, firmware, power settings, and allocation methods to provide the purchased work. We may perform maintenance, replace equipment, curtail load, or reroute hashrate, provided that we remain responsible for the term-level delivery obligation in Section 4.',
        },
        { kind: 'subheading', text: '10.2 Interruptions' },
        {
          kind: 'paragraph',
          text: 'Brief interruptions and fluctuations are inherent in mining. They do not independently create a refund right when the purchased cumulative work is delivered over the term. For a prolonged interruption within our reasonable control that causes a delivery shortfall, Section 4.3 applies.',
        },
        { kind: 'subheading', text: '10.3 Force majeure' },
        {
          kind: 'paragraph',
          text: 'We are not liable for delay or failure caused by events beyond our reasonable control, including utility outages or curtailments, grid emergencies, internet or telecommunications failures, pool or upstream-provider failures, severe weather, fire, flood, natural disaster, war, terrorism, civil unrest, labor disruption, epidemic, government action, changes in law, sanctions, equipment shortages, or widespread blockchain or network incidents. We may suspend performance for the affected period. If such an event makes performance commercially impracticable for more than 30 consecutive days, either party may end the remaining affected term, and we will provide an extension, credit, or refund for verified undelivered work as required by applicable law.',
        },
      ],
    },
    {
      heading: '11. Prohibited Conduct',
      blocks: [
        {
          kind: 'paragraph',
          text: 'You may not use the service to violate law; evade sanctions; launder money; defraud another person; interfere with our systems, pool, or other customers; submit false payment or identity information; access another customer’s account; reverse engineer or manipulate allocation records; misuse promotional offers; or resell the service while misrepresenting its source, performance, or risks.',
        },
        {
          kind: 'paragraph',
          text: 'We may suspend or terminate service for a material violation. When reasonably possible, we will limit suspension to what is necessary to protect customers, systems, payment integrity, or legal compliance.',
        },
      ],
    },
    {
      heading: '12. Disclaimers and Limitation of Liability',
      blocks: [
        { kind: 'subheading', text: '12.1 Service disclaimer' },
        {
          kind: 'paragraph',
          text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” EXCEPT FOR THE EXPRESS DELIVERY COMMITMENT IN SECTION 4, METERED MINING DISCLAIMS IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, THAT A BLOCK WILL BE FOUND, OR THAT BITCOIN WILL HAVE ANY PARTICULAR VALUE.',
        },
        { kind: 'subheading', text: '12.2 Excluded damages' },
        {
          kind: 'paragraph',
          text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, METERED MINING AND ITS MEMBERS, MANAGERS, EMPLOYEES, CONTRACTORS, AND AFFILIATES WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES; LOST PROFITS OR REVENUE; LOSS OF DIGITAL ASSETS; LOSS OF DATA; OR LOSS CAUSED BY A CUSTOMER-SUPPLIED ADDRESS, LOST PRIVATE KEY, MARKET MOVEMENT, NETWORK-DIFFICULTY CHANGE, POOL ACTION, OR BLOCKCHAIN EVENT.',
        },
        { kind: 'subheading', text: '12.3 Liability cap' },
        {
          kind: 'paragraph',
          text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY ARISING FROM A PARTICULAR ORDER WILL NOT EXCEED THE AMOUNT YOU PAID TO METERED MINING FOR THAT ORDER.',
        },
        {
          kind: 'paragraph',
          text: 'Some jurisdictions do not allow certain disclaimers or liability limits. In those jurisdictions, these provisions apply only to the fullest extent permitted by law. Nothing in these Terms excludes liability or remedies that cannot legally be excluded.',
        },
      ],
    },
    {
      heading: '13. Indemnification',
      blocks: [
        {
          kind: 'paragraph',
          text: 'To the extent permitted by law, you agree to defend, indemnify, and hold harmless Metered Mining and its members, managers, employees, contractors, and affiliates from third-party claims, damages, losses, and reasonable expenses arising from your unlawful use of the service, breach of these Terms, infringement of another person’s rights, or use of a payout address you do not have authority to use. This obligation does not apply to the extent a claim was caused by Metered Mining’s negligence, willful misconduct, or breach of these Terms.',
        },
      ],
    },
    {
      heading: '14. Changes to the Service or These Terms',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We may modify these Terms for future orders. The version accepted at checkout governs an existing fixed-term order unless a change is required by law, improves customer rights, or addresses a new feature you choose to use. The effective date appears at the top of these Terms.',
        },
      ],
    },
    {
      heading: '15. Governing Law and Disputes',
      blocks: [
        {
          kind: 'paragraph',
          text: 'These Terms are governed by the laws of the State of Wisconsin, without regard to conflict-of-law principles. Any lawsuit arising from these Terms or the service must be brought in the state courts located in Pierce County, Wisconsin, or, if federal subject-matter jurisdiction exists, the United States District Court serving Pierce County, Wisconsin. Each party consents to the personal jurisdiction of those courts and waives objections to venue there.',
        },
        {
          kind: 'paragraph',
          text: 'Before filing a claim, you and Metered Mining agree to send written notice describing the dispute and requested resolution and allow at least 30 days for an informal resolution. This requirement does not prevent either party from seeking emergency injunctive relief or using an applicable small-claims process.',
        },
      ],
    },
    {
      heading: '16. General Terms',
      blocks: [
        {
          kind: 'paragraph',
          text: 'These Terms, the applicable order, and incorporated policies are the entire agreement concerning the service. If an order expressly conflicts with these Terms, the order controls for that purchase. You may not assign your rights without our written consent; we may assign these Terms in connection with a merger, financing, reorganization, or transfer of the applicable business. If a provision is unenforceable, it will be limited or severed to the minimum extent necessary, and the remaining provisions remain effective. A waiver must be in writing and applies only to the stated instance. Headings are for convenience only. Electronic notices and records satisfy writing requirements to the extent permitted by law.',
        },
      ],
    },
    {
      heading: '17. Contact Information',
      blocks: [
        {
          kind: 'address',
          lines: [
            'The Metered, LLC d/b/a Metered Mining',
            '3469 Lake Elmo Ave North, Unit 452',
            'Lake Elmo, Minnesota 55042',
            'hello@meteredmining.io',
            'https://meteredmining.io',
          ],
        },
      ],
    },
  ],
};

// --- Privacy Policy ----------------------------------------------------------

export const PRIVACY: LegalDocumentContent = {
  title: 'Metered Mining Privacy Policy',
  dateLabel: 'Last updated',
  date: 'August 9, 2026',
  intro: [
    {
      kind: 'paragraph',
      text: 'This Privacy Policy explains how The Metered, LLC, doing business as Metered Mining (“Metered Mining,” “we,” “us,” or “our”), collects, uses, discloses, and protects personal information when you visit meteredmining.io or a Metered Mining subdomain, create an account, purchase or use a Virtual Hashrate rental, communicate with us, or otherwise use our websites, dashboards, products, and services (collectively, the “Services”).',
    },
    {
      kind: 'paragraph',
      text: 'For purposes of this Privacy Policy, “personal information” means information that identifies, relates to, describes, or could reasonably be linked with an individual or household. It does not include information that has been aggregated or deidentified so that it cannot reasonably be linked to you.',
    },
  ],
  sections: [
    {
      heading: '1. Information We Collect',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The information we collect depends on how you interact with the Services.',
        },
        { kind: 'subheading', text: '1.1 Information you provide directly' },
        { kind: 'paragraph', text: 'We may collect:' },
        {
          kind: 'list',
          items: [
            {
              lead: 'Contact information',
              rest: ', including your name, email address, telephone number, billing address, and mailing address.',
            },
            {
              lead: 'Account information',
              rest: ', including your username, encrypted or hashed password, account preferences, authentication information, and account-security records.',
            },
            {
              lead: 'Order and transaction information',
              rest: ', including the Virtual Hashrate product purchased, hashrate, rental duration, purchase amount, payment status, transaction identifiers, invoices, taxes, discounts, and order history.',
            },
            {
              lead: 'Bitcoin information',
              rest: ', including the Bitcoin payout address you provide, address-change history, mining allocation records, pool statistics, shares, best difficulty, block-related records, and any Bitcoin transaction identifier associated with a reward or payment.',
            },
            {
              lead: 'Payment-related information',
              rest: ', including billing details, payment method type, payment confirmation, and limited payment metadata provided to us by SaturnShift.io or an underlying payment provider. Complete card or bank-account credentials are generally collected and processed by the payment provider rather than Metered Mining.',
            },
            {
              lead: 'Identity and compliance information',
              rest: ', if reasonably required to verify your identity, payment authorization, location, sanctions status, fraud risk, tax status, or lawful eligibility to use the Services.',
            },
            {
              lead: 'Communications and support information',
              rest: ', including emails, support requests, survey responses, feedback, and other information you choose to provide.',
            },
            {
              lead: 'Marketing preferences',
              rest: ', including whether you want to receive promotional communications from us.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'You may choose not to provide certain information, but doing so may prevent us from creating an account, processing an order, delivering mining work, paying a reward, verifying a transaction, or providing another part of the Services.',
        },
        { kind: 'subheading', text: '1.2 Information collected automatically' },
        {
          kind: 'paragraph',
          text: 'When you use the Services, we and our service providers may automatically collect:',
        },
        {
          kind: 'list',
          items: [
            'Internet Protocol address;',
            'browser type, device type, operating system, and device identifiers;',
            'approximate location derived from an IP address;',
            'pages viewed, links selected, referring page, session duration, and timestamps;',
            'login activity, account events, error logs, and security events; and',
            'cookie identifiers and similar technical information.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'We may collect this information through server logs, cookies, pixels, software development kits, and similar technologies.',
        },
        { kind: 'subheading', text: '1.3 Information received from other sources' },
        { kind: 'paragraph', text: 'We may receive information from:' },
        {
          kind: 'list',
          items: [
            {
              lead: 'SaturnShift.io and underlying payment providers',
              rest: ', such as payment status, transaction identifiers, fraud indicators, billing information, refunds, disputes, and chargebacks;',
            },
            {
              lead: 'PowerPool.io',
              rest: ', such as payout-address information, mining shares, difficulty, delivered work, block-validation data, pool fees, payout information, and operational records;',
            },
            {
              lead: 'Website, hosting, analytics, communications, customer-support, security, and fraud-prevention providers',
              rest: ';',
            },
            {
              lead: 'Public blockchains',
              rest: ', including publicly available Bitcoin addresses, transactions, block data, and associated timestamps; and',
            },
            {
              lead: 'Public records, compliance databases, or verification providers',
              rest: ', when necessary for fraud prevention, sanctions screening, legal compliance, or protection of the Services.',
            },
          ],
        },
      ],
    },
    {
      heading: '2. How We Use Personal Information',
      blocks: [
        { kind: 'paragraph', text: 'We may use personal information to:' },
        {
          kind: 'list',
          items: [
            'create, authenticate, secure, maintain, and administer customer accounts;',
            'process orders and payments and issue transaction confirmations and invoices;',
            'activate, allocate, measure, and deliver purchased Virtual Hashrate;',
            'direct mining work to the Bitcoin payout address associated with an order;',
            'display hashrate, share, difficulty, block, transaction, and order information in a customer dashboard;',
            'coordinate mining activity and reward processing with PowerPool;',
            'communicate about an account, order, payment, service interruption, security event, policy update, or support request;',
            'verify identity, payment authorization, location, account ownership, or payout-address changes;',
            'detect, investigate, prevent, and respond to fraud, chargebacks, security incidents, prohibited activity, or misuse;',
            'troubleshoot, analyze, maintain, personalize, and improve the Services;',
            'measure website and service performance and understand how customers use the Services;',
            'send marketing communications where permitted by law and consistent with your preferences;',
            'comply with tax, accounting, sanctions, anti-fraud, court-order, regulatory, and other legal obligations;',
            'establish, exercise, or defend legal claims and enforce our agreements; and',
            'complete a merger, financing, acquisition, reorganization, sale of assets, bankruptcy, or similar business transaction.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'We may aggregate or deidentify information and use it for analytics, capacity planning, operational reporting, research, product development, and other lawful business purposes. We will not attempt to reidentify information that we maintain as deidentified unless permitted by law for testing whether our deidentification methods are effective.',
        },
      ],
    },
    {
      heading: '3. How We Disclose Personal Information',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We may disclose personal information to the following categories of recipients for the purposes described in this Policy:',
        },
        {
          kind: 'list',
          items: [
            {
              lead: 'Payment and commerce providers',
              rest: ', including SaturnShift and any underlying bank, card network, payment processor, fraud-prevention provider, invoicing provider, or tax service involved in a transaction.',
            },
            {
              lead: 'Mining infrastructure and pool providers',
              rest: ', including PowerPool, when necessary to allocate mining work, associate a payout address, verify delivery, validate a block, calculate a pool fee, or facilitate a reward.',
            },
            {
              lead: 'Operational service providers',
              rest: ', including cloud hosting, website hosting, analytics, communications, email, customer support, cybersecurity, authentication, data storage, and professional-services providers.',
            },
            {
              lead: 'Professional advisers',
              rest: ', including attorneys, accountants, auditors, insurers, consultants, and financial advisers.',
            },
            {
              lead: 'Government authorities and other legally authorized parties',
              rest: ', when we reasonably believe disclosure is necessary to comply with law, legal process, a court order, sanctions requirements, or a lawful government request; protect rights, safety, and security; prevent fraud or abuse; or enforce an agreement.',
            },
            {
              lead: 'Parties to a business transaction',
              rest: ', such as a potential or actual buyer, investor, lender, successor, or adviser in connection with a merger, financing, acquisition, reorganization, bankruptcy, or sale of all or part of our business or assets.',
            },
            { lead: 'Other parties at your direction or with your consent', rest: '.' },
          ],
        },
        {
          kind: 'paragraph',
          text: 'We do not sell personal information for money. Metered Mining does not knowingly disclose personal information for cross-context behavioral advertising or targeted advertising as those terms are defined by applicable state privacy laws. If our practices change, we will update this Policy and provide any required opt-out mechanism before engaging in that activity.',
        },
        {
          kind: 'paragraph',
          text: 'We do not use sensitive personal information to infer characteristics about you.',
        },
      ],
    },
    {
      heading: '4. Bitcoin Addresses and Public Blockchain Information',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The Bitcoin blockchain is public. Bitcoin addresses, transactions, amounts, timestamps, and related blockchain information may be permanently visible to anyone and may not be altered or deleted by Metered Mining. Although a Bitcoin address does not necessarily identify you by name on its own, it may become associated with you through other information.',
        },
        {
          kind: 'paragraph',
          text: 'When you give us a Bitcoin address, we may disclose it to PowerPool and use it to provide the Services. A privacy request submitted to Metered Mining cannot remove or change information already recorded on a public blockchain, reverse a Bitcoin transaction, or require an unaffiliated pool, wallet provider, blockchain participant, or third party to alter an immutable record.',
        },
        {
          kind: 'paragraph',
          text: 'Do not send us a wallet seed phrase, private key, or wallet password. Metered Mining does not need this information to provide a Virtual Hashrate rental and will never ask you for it.',
        },
      ],
    },
    {
      heading: '5. Cookies and Similar Technologies',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We and our service providers may use cookies and similar technologies to:',
        },
        {
          kind: 'list',
          items: [
            'keep you signed in and maintain your session;',
            'remember settings and preferences;',
            'provide account, checkout, and security functionality;',
            'detect fraud and protect the Services; and',
            'understand website traffic and improve performance.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Some cookies are necessary for the Services to function. Where required by law, we will request consent before using nonessential cookies. You can control cookies through your browser settings and, if available, our cookie-preference tool. Blocking cookies may prevent some features from working correctly.',
        },
        {
          kind: 'paragraph',
          text: 'At this time, our response to browser-based opt-out preference signals, including Global Privacy Control, depends on the technologies implemented on the Site and applicable law. Where we are legally required and technically able to recognize such a signal, we will treat it as an opt-out request for the browser or device sending the signal. You may also submit a request using the contact information below.',
        },
      ],
    },
    {
      heading: '6. Data Retention',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We retain personal information only for as long as reasonably necessary for the purposes described in this Policy, including to provide the Services, maintain transaction and mining-delivery records, protect account security, resolve disputes, enforce agreements, prevent fraud, and satisfy legal, tax, accounting, and regulatory obligations.',
        },
        {
          kind: 'paragraph',
          text: 'Retention periods vary by category. For example, account information may be retained while an account remains active; transaction, invoice, payment, pool, and delivery records may be retained for the period required by tax, accounting, fraud-prevention, and contract laws; and security logs may be retained for a shorter period unless needed to investigate an incident. We may retain information longer when litigation, an investigation, a legal hold, or another lawful obligation requires it.',
        },
        {
          kind: 'paragraph',
          text: 'Public blockchain records are maintained by the Bitcoin network and may remain publicly available indefinitely.',
        },
      ],
    },
    {
      heading: '7. Data Security',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We use administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, loss, misuse, alteration, or disclosure. However, no system, transmission method, or storage environment can be guaranteed completely secure.',
        },
        {
          kind: 'paragraph',
          text: 'You are responsible for using a strong, unique password, protecting your account credentials, securing your email and Bitcoin wallet, and notifying us promptly at hello@meteredmining.io if you believe your account has been compromised. Do not send highly sensitive information through unsecured email.',
        },
      ],
    },
    {
      heading: '8. Your Privacy Rights',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Depending on your state or country of residence and whether an applicable privacy law covers Metered Mining, you may have the right to:',
        },
        {
          kind: 'list',
          items: [
            'confirm whether we process your personal information;',
            'access or obtain a copy of personal information we maintain about you;',
            'correct inaccurate personal information;',
            'request deletion of personal information, subject to legal exceptions;',
            'obtain certain information in a portable format;',
            'opt out of targeted advertising, a sale of personal information, or certain profiling;',
            'withdraw consent where processing is based on consent;',
            'appeal our denial of a privacy request; and',
            'receive equal service and not be discriminated against for exercising a privacy right.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'To submit a request, email hello@meteredmining.io with the subject line “Privacy Request.” Describe the right you want to exercise and the account or email address involved. We may request information reasonably necessary to verify your identity and authority. An authorized agent may submit a request where permitted by law, but we may require proof of authorization and direct verification from you.',
        },
        {
          kind: 'paragraph',
          text: 'We will respond within the period required by applicable law. Some information may be exempt from a request, including information needed to complete a transaction, provide a requested service, protect security, prevent fraud, comply with law, maintain tax or accounting records, establish or defend legal claims, or preserve a public blockchain record. If we deny a request, we will provide an explanation and any available appeal instructions when required.',
        },
        {
          kind: 'paragraph',
          text: 'You may appeal a denial by emailing hello@meteredmining.io with the subject line “Privacy Appeal” within 30 days after receiving our decision.',
        },
      ],
    },
    {
      heading: '9. Communication Preferences',
      blocks: [
        {
          kind: 'paragraph',
          text: 'You may unsubscribe from promotional emails by using the unsubscribe link in the message or contacting hello@meteredmining.io. Even if you opt out of marketing, we may continue sending nonpromotional communications concerning your account, orders, payments, mining activity, security, legal notices, or customer support.',
        },
      ],
    },
    {
      heading: '10. Children’s Privacy',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The Services are intended only for individuals who are at least 18 years old. We do not knowingly collect personal information from children under 13, and we do not knowingly sell or use for targeted advertising the personal information of anyone under 18. If you believe a child provided personal information to us, contact hello@meteredmining.io so we can investigate and take appropriate action.',
        },
      ],
    },
    {
      heading: '11. Third-Party Websites and Services',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The Services may link to or integrate with websites and services operated by third parties, including SaturnShift, PowerPool, wallet providers, blockchain explorers, and social platforms. Their privacy policies govern information they collect and control. We are not responsible for the privacy, security, accuracy, or operation of unaffiliated third-party services.',
        },
      ],
    },
    {
      heading: '12. International Users',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Metered Mining is based in the United States. If you access the Services from another country, your personal information may be transferred to, stored in, and processed in the United States and other countries where our service providers operate. Those countries may have privacy laws different from the laws where you live.',
        },
        {
          kind: 'paragraph',
          text: 'If applicable law requires a particular safeguard for an international transfer, we will use an approved transfer mechanism or another lawful basis. Availability of the Services in a country does not mean that Metered Mining is registered or authorized to offer every Service there.',
        },
      ],
    },
    {
      heading: '13. Changes to This Privacy Policy',
      blocks: [
        {
          kind: 'paragraph',
          text: 'We may update this Privacy Policy to reflect changes in the Services, our practices, technology, or legal requirements. We will post the updated Policy and change the “Last updated” date. If required by law, we will provide additional notice or obtain consent before a material change takes effect.',
        },
      ],
    },
    {
      heading: '14. Contact Us',
      blocks: [
        {
          kind: 'paragraph',
          text: 'For questions, complaints, or privacy-rights requests, contact:',
        },
        {
          kind: 'address',
          lines: [
            'The Metered, LLC d/b/a Metered Mining',
            '3469 Lake Elmo Ave North, Unit 452',
            'Lake Elmo, Minnesota 55042',
            'Email: hello@meteredmining.io',
            'Website: https://meteredmining.io',
          ],
        },
        {
          kind: 'paragraph',
          text: 'For purposes of applicable privacy laws, The Metered, LLC is the controller of the personal information described in this Policy unless another notice states otherwise.',
        },
      ],
    },
  ],
};
